import React, { useCallback, useState, useEffect } from "react";
import {
  MainContainer,
  ChatContainer,
  MessageList,
  TypingIndicator,
  Message,
  MessageInput,
  ConversationHeader,
  Avatar,
} from "@chatscope/chat-ui-kit-react";
import ReactMarkdown from "react-markdown";
import { useChat, Message as ChatMessage } from "./useChat";
import Select from "react-select";
import { WelcomeMessages } from "./components/WelcomeMessages";
import { SessionExpiredMessage } from "./components/SessionExpiredMessage";
import { ReviewMessage } from "./components/ReviewMessage";
import { NotHelpfulMessage } from "./components/NotHelpfulMessage";
import { ThankYouMessage } from "./components/ThankYouMessage";
import { LanguageContext } from "./LanguageContext";
import { translations, Language, TranslationKey } from "./translations";

import "./App.css";
import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";

// Utility function to detect if user is a partner (has email in URL)
function isPartnerMode(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  return !!urlParams.get('email');
}

function App() {
  const options = [
    { value: "ar", label: "Arabic" },
    { value: "ch", label: "Chinese" },
    { value: "dh", label: "Dutch" },
    { value: "en", label: "English" },
    { value: "fr", label: "French" },
    { value: "gr", label: "German" },
    { value: "it", label: "Italian" },
    { value: "jp", label: "Japanese" },
    { value: "ko", label: "Korean" },
    { value: "pr", label: "Portuguese" },
    { value: "sp", label: "Spanish" },
  ];

  const getLocaleLabel = (value: string) => {
    return options.find((option) => option.value === value)?.label || value;
  };
  const [locale, setLocale] = useState("en");
  const [showChat, setShowChat] = useState(false);
  const [isInputAvailable, setIsInputAvailable] = useState(false);
  const [typing, setTyping] = useState(false);
  const {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    isStreaming,
    isSessionExpired,
    waitingForReview,
    handleReviewSubmit,
    sessionId,
    setMessages,
    currentContact,
    isMachineSerialVerified,
  } = useChat({
    body: {
      language: getLocaleLabel(locale),
    },
    onStreamStart: () => setTyping(true),
    onStreamEnd: () => setTyping(false),
  });

  // Add ref for MessageList
  const messageListRef = React.useRef<HTMLDivElement>(null);

  // Add effect to scroll to bottom when messages change or during streaming
  React.useEffect(() => {
    if (messageListRef.current) {
      const element = messageListRef.current as HTMLDivElement;
      element.scrollTop = element.scrollHeight;
    }

    // Add interval for continuous scrolling during streaming
    let scrollInterval: NodeJS.Timeout | null = null;
    if (isStreaming) {
      scrollInterval = setInterval(() => {
        if (messageListRef.current) {
          const element = messageListRef.current as HTMLDivElement;
          element.scrollTop = element.scrollHeight;
        }
      }, 100); // Check every 100ms
    }

    // Cleanup interval on unmount or when streaming stops
    return () => {
      if (scrollInterval) {
        clearInterval(scrollInterval);
      }
    };
  }, [messages, isStreaming]);

  // Show chat based on messages
  useEffect(() => {
    if (messages.length > 0) {
      setShowChat(true);
      setIsInputAvailable(true);
    }
  }, [messages]);

  const handlePrivacyAccept = useCallback(() => {
    setShowChat(true);
    setIsInputAvailable(true);
    
    // Only send "Hi" if we don't have existing verification
    // Check if we have email ref (URL email) or machine ID verification
    const urlParams = new URLSearchParams(window.location.search);
    const urlEmail = urlParams.get('email');
    const hasExistingVerification = urlEmail || isMachineSerialVerified;
    
    if (messages.length === 0 && !hasExistingVerification) {
      // No existing verification - send "Hi" to trigger email/machine ID collection flow
      handleSubmit("Hi", true);
    }
    // If hasExistingVerification is true, we'll show the hardcoded welcome message
    // and the chatbot will use the partner flow (no email collection needed)
  }, [setShowChat, handleSubmit, messages.length, isMachineSerialVerified]);

  const handleLocaleUpdate = (option: any) => {
    setLocale(option.value);
    localStorage.setItem("locale", option.value);
  };

  const handleReviewWithMessage = useCallback(
    async (wasHelpful: boolean, message: string) => {
      await handleReviewSubmit(wasHelpful);
      if (!wasHelpful) {
        const assistantMessage: ChatMessage = {
          id: new Date().getTime().toString(),
          role: "assistant",
          content: <NotHelpfulMessage />,
          isComponent: true,
        };
        setMessages((prev: ChatMessage[]): ChatMessage[] => [
          ...prev,
          assistantMessage,
        ]);
      } else {
        const assistantMessage: ChatMessage = {
          id: new Date().getTime().toString(),
          role: "assistant",
          content: <ThankYouMessage />,
          isComponent: true,
        };
        setMessages((prev: ChatMessage[]): ChatMessage[] => [
          ...prev,
          assistantMessage,
        ]);
      }
    },
    [handleReviewSubmit, setMessages]
  );

  const languageContextValue = {
    language: locale as Language,
    t: (key: TranslationKey) => {
      return translations[locale as Language]?.[key] || translations["en"][key];
    },
  };

  return (
    <LanguageContext.Provider value={languageContextValue}>
      <div style={{ height: "100vh", position: "relative" }}>
        <MainContainer responsive>
          <ChatContainer data-chat-container>
            <ConversationHeader>
              <Avatar src="/laf-logo.svg" name="Chatbot" />
              <ConversationHeader.Content userName={isPartnerMode() ? "Partner Chatbot" : "LAFly"} />
              <ConversationHeader.Actions>
                <ConversationHeader.Content>
                  {/* <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.8em",
                        opacity: 0.7,
                        fontFamily: "monospace",
                        display: "flex",
                        gap: "15px",
                      }}
                    >
                      <span>Session: {sessionId}</span>
                      <span>Contact: {currentContact?.id || "none"}</span>
                      <span>
                        Verified: {isMachineSerialVerified ? "✅" : "❌"}
                      </span>
                    </div> */}
                    <Select
                      options={options}
                      defaultValue={options.find(
                        (option) => option.value === locale
                      )}
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      className="select-locale"
                      onChange={handleLocaleUpdate}
                    />
                  {/* </div> */}
                </ConversationHeader.Content>
              </ConversationHeader.Actions>
            </ConversationHeader>

            <MessageList
              ref={messageListRef}
              typingIndicator={
                typing ? (
                  <TypingIndicator
                    content={languageContextValue.t("typingIndicator")}
                  />
                ) : null
              }
              style={{ paddingBottom: '40px' }}
            >
              {!showChat ? (
                <WelcomeMessages onAcceptPrivacy={handlePrivacyAccept} isPartnerMode={isPartnerMode()} />
              ) : (
                <>
                  {/* Hardcoded welcome message - show when no visible messages and has existing verification */}
                  {(() => {
                    const urlParams = new URLSearchParams(window.location.search);
                    const urlEmail = urlParams.get('email');
                    const hasExistingVerification = urlEmail || isMachineSerialVerified;
                    const hasVisibleMessages = messages.filter((message) => !message.dont_show).length > 0;
                    
                    return hasExistingVerification && !hasVisibleMessages;
                  })() && (
                    <Message
                      model={{
                        message: "Hello! How can I assist you today with Latte Art Factory's automated milk frothing solutions? If you have any questions or need help, feel free to ask!",
                        direction: "incoming",
                        position: "normal",
                      }}
                      avatarPosition="bl"
                    >
                      <Avatar src="/laf-logo.svg" name="LAFly" size="sm" />
                      <Message.CustomContent>
                        <ReactMarkdown
                          components={{
                            img: ({ src, alt }) => (
                              <div style={{ margin: '12px 0' }}>
                                {src && (
                                  <div style={{ 
                                    fontSize: '12px', 
                                    color: '#6b7280',
                                    wordBreak: 'break-all'
                                  }}>
                                    <a 
                                      href={src} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      style={{ 
                                        color: '#3b82f6',
                                        textDecoration: 'underline'
                                      }}
                                    >
                                      {alt || 'Image Link'}: {src}
                                    </a>
                                  </div>
                                )}
                              </div>
                            ),
                            a: ({ href, children, ...props }) => {
                              // Check if it's a YouTube link
                              const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
                              const match = href?.match(youtubeRegex);
                              
                              if (match && match[1]) {
                                const videoId = match[1];
                                return (
                                  <div style={{ margin: '16px 0' }}>
                                    <iframe
                                      width="100%"
                                      height="200"
                                      src={`https://www.youtube.com/embed/${videoId}`}
                                      title="YouTube video player"
                                      frameBorder="0"
                                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                      allowFullScreen
                                      style={{ 
                                        maxWidth: '400px',
                                        borderRadius: '8px'
                                      }}
                                    ></iframe>
                                  </div>
                                );
                              }
                              
                              // Regular link
                              return (
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  {...props}
                                >
                                  {children}
                                </a>
                              );
                            },
                          }}
                          className="chat-markdown"
                        >
                          Hello! How can I assist you today with Latte Art Factory's automated milk frothing solutions? If you have any questions or need help, feel free to ask!
                        </ReactMarkdown>
                      </Message.CustomContent>
                    </Message>
                  )}
                  
                  {/* Regular messages */}
                  {messages
                    .filter((message) => !message.dont_show)
                    .filter(
                      (message) =>
                        (typeof message.content === "string"
                          ? message.content.length > 0
                          : true) || message.isComponent
                    )
                    .map((message) => (
                      <Message
                        key={message.id}
                        model={{
                          ...message,
                          direction:
                            message.role === "user" ? "outgoing" : "incoming",
                          position: "normal",
                        }}
                        avatarPosition="bl"
                      >
                        {message.role === "assistant" && (
                          <Avatar src="/laf-logo.svg" name="LAFly" size="sm" />
                        )}
                        <Message.CustomContent>
                          {message.isComponent ? (
                            message.content
                          ) : (
                            <ReactMarkdown
                              components={{
                                img: ({ src, alt }) => (
                                  <img
                                    src={src}
                                    alt={alt}
                                    style={{
                                      maxWidth: '100%',
                                      height: 'auto',
                                      borderRadius: '8px',
                                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                      margin: '12px 0',
                                      display: 'block'
                                    }}
                                    loading="lazy"
                                  />
                                ),
                                a: ({ href, children, ...props }) => {
                                  // Check if it's a YouTube link
                                  const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
                                  const match = href?.match(youtubeRegex);
                                  
                                  if (match && match[1]) {
                                    const videoId = match[1];
                                    return (
                                      <div style={{ margin: '16px 0' }}>
                                        <iframe
                                          width="100%"
                                          height="200"
                                          src={`https://www.youtube.com/embed/${videoId}`}
                                          title="YouTube video player"
                                          frameBorder="0"
                                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                          allowFullScreen
                                          style={{ 
                                            maxWidth: '400px',
                                            borderRadius: '8px'
                                          }}
                                        ></iframe>
                                      </div>
                                    );
                                  }
                                  
                                  // Regular link
                                  return (
                                    <a
                                      href={href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      {...props}
                                    >
                                      {children}
                                    </a>
                                  );
                                },
                              }}
                              className={`chat-markdown ${message.streaming ? 'streaming-content' : ''}`}
                            >
                              {typeof message.content === "string"
                                ? message.content
                                : ""}
                            </ReactMarkdown>
                          )}
                        </Message.CustomContent>
                      </Message>
                    ))
                  }
                </>
              )}

              {isSessionExpired && (
                <SessionExpiredMessage
                  onRefresh={() => window.location.reload()}
                />
              )}

              {waitingForReview && (
                <ReviewMessage onReviewSubmit={handleReviewWithMessage} />
              )}
            </MessageList>

            <MessageInput
              value={input}
              onChange={(_, textContent) => handleInputChange(textContent)}
              attachButton={false}
              placeholder={
                waitingForReview
                  ? languageContextValue.t("feedbackMessage")
                  : isSessionExpired
                  ? languageContextValue.t("sessionExpired")
                  : languageContextValue.t("inputPlaceholder")
              }
              onSend={() => handleSubmit(input)}
              disabled={
                !isInputAvailable || isSessionExpired
              }
              sendDisabled={
                !isInputAvailable || isSessionExpired
              }
            />
          </ChatContainer>
        </MainContainer>
      </div>
    </LanguageContext.Provider>
  );
}

export default App;
